import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { PhotoEditorDecorator, PhotoEditorState } from './photo-editor.decorator';
import { DialogService } from '../popup/dialog-service/dialog.service';
import { AutoUnsubscribe } from '../auto-unsubscribe.decorator';
import { Subscription } from 'rxjs';
import { SnackService } from '../snack.service';
import { PhotoEditorPen, TextAnnotation, UserAnnotation } from './annotation';
import { EmitterEvent } from '../emitter.service';
import { CanvasZoomState } from '../rendering';
import { ResizeObserverDirective } from '../resize/resize-observer.directive';

const DEFAULT_PEN_COLOR = '#FF0000';
const FONT_SIZES = [10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];

@AutoUnsubscribe()
@Component({
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatInputModule, MatSelectModule, MatOptionModule, MatTooltipModule, TranslatePipe, ResizeObserverDirective],
  selector: 'app-photo-editor',
  templateUrl: './photo-editor.component.html',
  styleUrls: ['./photo-editor.component.scss'],
  standalone: true,
})
export class PhotoEditorComponent implements OnDestroy {
  private canvasRef: ElementRef<HTMLCanvasElement> = null;
  private _decorator: PhotoEditorDecorator = null;
  private _imageSrc: string = null;
  private _userAnnotations: UserAnnotation[] = [];
  private pendingLoader: HTMLImageElement | null = null;

  canReset = false;
  canZoomIn = false;
  canZoomOut = false;
  canRedo = false;
  canUndo = false;
  savingAnnotations = false;

  @Input()
  saveAction: (userAnnotations: UserAnnotation[]) => Promise<void> = undefined;

  @Output()
  editorClose: EventEmitter<boolean> = new EventEmitter<boolean>();

  private textAnnotation: TextAnnotation = null;
  editingAnnotation: TextAnnotation = null;

  private zoomStateSub: Subscription = null;
  private stateSub: Subscription = null;
  private promptSub: Subscription = null;

  constructor(
    private readonly dialogService: DialogService,
    private readonly snackService: SnackService,
  ) {}

  @Input()
  set src(src: string) {
    this._imageSrc = src;

    if (this._decorator) {
      this.loadImage();
    }
  }

  @Input()
  set annotations(annotations: UserAnnotation[]) {
    this._userAnnotations = annotations ? [...annotations] : [];
  }

  @ViewChild('canvas')
  set canvas(canvasRef: ElementRef<HTMLCanvasElement>) {
    if (!canvasRef) {
      return;
    }

    if (this.canvasRef !== canvasRef) {
      console.log(`Initializing decorator.`, this.src);
      this._decorator?.destroy();
      this.zoomStateSub?.unsubscribe();

      this._decorator = new PhotoEditorDecorator(canvasRef.nativeElement, DEFAULT_PEN_COLOR);
      this.zoomStateSub = this._decorator.zoomChanged.subscribe((state: EmitterEvent<CanvasZoomState>) => {
        this.canReset = state.value.canReset;
        this.canZoomIn = state.value.canZoomIn;
        this.canZoomOut = state.value.canZoomOut;
      });

      this.promptSub?.unsubscribe();
      this.promptSub = this._decorator.$promptForUserInput.subscribe((a: TextAnnotation) => {
        this.textAnnotation = a;
        this.editingAnnotation = { ...a };

        // Max-width of prompt + width of toolbar
        const maxXPosition = window.innerWidth - 475 - 50;
        if (a.screenOrigin.x > maxXPosition) {
          this.editingAnnotation.screenOrigin.x = maxXPosition > 0 ? maxXPosition : 0;
        }

        setTimeout(() => document.getElementById('userTextInput')?.focus(), 500);
      });

      this.stateSub?.unsubscribe();
      this.stateSub = this._decorator.$state.subscribe((state: PhotoEditorState) => {
        this.canRedo = state.redoCnt > 0;
        this.canUndo = state.undoCnt > 0;
      });

      this.loadImage();

      this.canvasRef = canvasRef;
    }
  }

  get currentColor(): string {
    return this._decorator?.currentColor || DEFAULT_PEN_COLOR;
  }

  get currentPen(): PhotoEditorPen {
    return this._decorator?.currentPen || null;
  }

  closeEditor(): void {
    if (this._decorator.hasChanges()) {
      this.dialogService.showConfirm('uix.photo.editor.nav.confirm').then((confirmed: boolean) => {
        if (confirmed) {
          this.editorClose.next(true);
        }
      });
    } else {
      this.editorClose.next(true);
    }
  }

  closePrompt(): void {
    if (this.editingAnnotation) {
      this.textAnnotation.text = this.editingAnnotation.text;
      this.textAnnotation.fontSize = this.editingAnnotation.fontSize;
      this.textAnnotation.color = this.editingAnnotation.color;

      this.stopDrawing(false);
    }
  }

  @HostListener('window:keyup.Escape', ['$event'])
  cancelPrompt(): void {
    this.textAnnotation = null;
    this.editingAnnotation = null;
    this._decorator.cancelEditing(false);
  }

  fontSizes(): number[] {
    return FONT_SIZES;
  }

  hasChanges(): boolean {
    return this._decorator?.hasChanges() || false;
  }

  resetZoom(): void {
    this._decorator.resetZoom(true);
  }

  zoomIn(): void {
    this._decorator.zoomIn();
  }

  zoomOut(): void {
    this._decorator.zoomOut();
  }

  canvasResized(width: number, height: number): void {
    this._decorator?.resize(width, height);
  }

  updateColor(event: Event): void {
    const color = (event.target as HTMLInputElement).value;
    this._decorator.updateColor(color);

    if (this.editingAnnotation) {
      this.editingAnnotation.color = color;
    }
  }

  saveDrawingBtnAction(): void {
    this.doSaveDrawing();
  }

  @HostListener('window:keydown.Meta.S', ['$event'])
  @HostListener('window:keydown.Control.S', ['$event'])
  doSaveDrawing(event?: KeyboardEvent): void {
    this.handleKeyEventOrDefault(() => {
      if (this.saveAction) {
        this.savingAnnotations = true;
        this.saveAction(this._decorator.userAnnotations).then(
          () => {
            this.snackService.snackThis('uix.photo.editor.message.save.success');
            this._decorator.clearUndoRedoStacks();
            this.savingAnnotations = false;
          },
          (err) => {
            this.snackService.snackThis('uix.photo.editor.message.save.error', {}, err);
            this.savingAnnotations = false;
          },
        );
      }
    }, event);
  }

  startDrawing(pen: PhotoEditorPen): void {
    this._decorator.updatePen(pen);
  }

  stopDrawing(clearPen = true): void {
    this.textAnnotation = null;
    this.editingAnnotation = null;
    this._decorator.stopEditing(clearPen);
  }

  @HostListener('window:keydown.Meta.Y', ['$event'])
  @HostListener('window:keydown.Control.Y', ['$event'])
  redo(event?: KeyboardEvent): void {
    this.handleKeyEventOrDefault(() => this._decorator.redo(), event);
  }

  @HostListener('window:keydown.Meta.Z', ['$event'])
  @HostListener('window:keydown.Control.Z', ['$event'])
  undo(event?: KeyboardEvent): void {
    this.handleKeyEventOrDefault(() => this._decorator.undo(), event);
  }

  ngOnDestroy(): void {
    if (this.pendingLoader) {
      this.pendingLoader.onload = null;
      this.pendingLoader.onerror = null;
      this.pendingLoader = null;
    }

    this._decorator?.destroy();
  }

  private loadImage(): void {
    if (this.pendingLoader) {
      this.pendingLoader.onload = null;
      this.pendingLoader.onerror = null;
      this.pendingLoader = null;
    }

    const imgLoader = document.createElement('img');
    this.pendingLoader = imgLoader;
    imgLoader.src = this._imageSrc;
    imgLoader.onload = () => {
      imgLoader.onload = null;
      imgLoader.onerror = null;
      if (imgLoader === this.pendingLoader) {
        this._decorator.image = imgLoader;
        this._decorator.annotations = this._userAnnotations;
        this.pendingLoader = null;
      }
    };
    imgLoader.onerror = (err) => {
      imgLoader.onload = null;
      imgLoader.onerror = null;
      console.error('Failed to load image', err);
      if (imgLoader === this.pendingLoader) {
        this.pendingLoader = null;
      }
    };
  }

  private handleKeyEventOrDefault(task: () => void, event?: KeyboardEvent): void {
    if (!event || this.keyboardEventIsValid(event)) {
      task();
    }

    if (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  private keyboardEventIsValid(event: KeyboardEvent): boolean {
    const isMac = window.navigator.userAgent.includes('Mac');
    const pomKeyValid = event?.metaKey && isMac;
    const ctrlKeyValid = event?.ctrlKey && !isMac;

    return pomKeyValid || ctrlKeyValid;
  }
}
